<template>
  <div class="user-departments-container">
    <div class="box">
      <h3 class="title is-5 has-text-info">Sektori koje korisnik vidi</h3>
      <p class="subtitle is-6">
        Dodajte sektore koje ovaj korisnik može vidjeti. Ako je korisnik
        predradnik (nivo 1), možete specificirati koje zaposlenike unutar
        sektora može vidjeti.
      </p>

      <!-- Loading indicator -->
      <div v-if="loading" class="has-text-centered">
        <span class="icon is-large">
          <i class="fas fa-spinner fa-spin"></i>
        </span>
        <p>Učitavanje...</p>
      </div>

      <!-- Error message -->
      <div v-if="errorMessage" class="notification is-danger is-light">
        {{ errorMessage }}
      </div>

      <!-- Success message -->
      <div v-if="successMessage" class="notification is-success is-light">
        {{ successMessage }}
      </div>

      <!-- Existing user departments -->
      <div v-if="!loading && userDepartments.length > 0" class="mb-4">
        <table class="table is-fullwidth is-striped is-hoverable">
          <thead>
            <tr>
              <th>Sektor</th>
              <th>Nivo</th>
              <th>Napomena</th>
              <th>Odabrani zaposlenici</th>
              <th>Akcije</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ud in userDepartments" :key="ud.userDepartmentId">
              <td>{{ ud.departmentName }}</td>
              <td>
                <span
                  class="tag"
                  :class="ud.levelValue === 1 ? 'is-warning' : 'is-info'"
                >
                  {{ ud.levelValue === 1 ? "Predradnik" : "Standardni" }}
                </span>
              </td>
              <td>{{ ud.note || "-" }}</td>
              <td>
                <span v-if="ud.subdepartmentIds">
                  {{ getEmployeeNamesFromIds(ud.subdepartmentIds) }}
                </span>
                <span v-else class="has-text-grey">Svi zaposlenici</span>
              </td>
              <td>
                <button
                  class="button is-small is-info is-light mr-1"
                  @click="editUserDepartment(ud)"
                  title="Uredi"
                >
                  <span class="icon">
                    <font-awesome-icon icon="edit" />
                  </span>
                </button>
                <button
                  class="button is-small is-danger is-light"
                  @click="confirmDeleteUserDepartment(ud)"
                  title="Obriši"
                >
                  <span class="icon">
                    <font-awesome-icon icon="trash-alt" />
                  </span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- No departments message -->
      <div
        v-if="!loading && userDepartments.length === 0"
        class="notification is-warning is-light"
      >
        Korisnik nema dodijeljenih sektora. Dodajte barem jedan sektor.
      </div>

      <hr />

      <!-- Add/Edit form -->
      <div class="add-department-form">
        <h4 class="title is-6">
          {{ isEditing ? "Uredi sektor" : "Dodaj novi sektor" }}
        </h4>

        <!-- Department selection -->
        <div class="field">
          <label class="label">Sektor</label>
          <div class="control">
            <vue-select
              :disabled="isEditing"
              class="has-background-info-light"
              label="name"
              :clearable="false"
              :options="availableDepartments"
              v-model="selectedDepartment"
              placeholder="Odaberite sektor..."
              @input="onDepartmentChange"
            />
          </div>
        </div>

        <!-- Level selection -->
        <div class="field">
          <label class="label">Nivo pristupa</label>
          <div class="control">
            <div class="select is-fullwidth">
              <select v-model="formData.levelValue">
                <option :value="0">Standardni (vidi sve u sektoru)</option>
                <option :value="1">
                  Predradnik (vidi samo odabrane zaposlenike)
                </option>
              </select>
            </div>
          </div>
          <p class="help">
            Predradnik (nivo 1) vidi samo zaposlenike koje mu dodijelite ispod.
          </p>
        </div>

        <!-- Employee selection (only if level is 1) -->
        <div v-if="formData.levelValue === 1" class="field">
          <label class="label">Zaposlenici koje može vidjeti</label>
          <div class="control">
            <vue-select
              multiple
              class="has-background-info-light"
              label="fullName"
              :options="departmentEmployees"
              v-model="selectedEmployees"
              placeholder="Odaberite zaposlenike..."
              :loading="loadingEmployees"
            />
          </div>
          <p class="help">
            Ostavite prazno ako želite da vidi sve zaposlenike u sektoru.
          </p>
        </div>

        <!-- Note -->
        <div class="field">
          <label class="label">Napomena (opcionalno)</label>
          <div class="control">
            <input
              class="input has-background-info-light"
              type="text"
              v-model="formData.note"
              placeholder="Napomena..."
            />
          </div>
        </div>

        <!-- Action buttons -->
        <div class="field is-grouped">
          <div class="control">
            <button
              class="button is-info"
              :disabled="!canSave"
              :class="{ 'is-loading': saving }"
              @click="saveUserDepartment"
            >
              {{ isEditing ? "Sačuvaj izmjene" : "Dodaj sektor" }}
            </button>
          </div>
          <div class="control" v-if="isEditing">
            <button class="button is-light" @click="cancelEdit">
              Odustani
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete confirmation modal -->
    <div class="modal" :class="{ 'is-active': showDeleteModal }">
      <div class="modal-background" @click="showDeleteModal = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Potvrda brisanja</p>
          <button
            class="delete"
            aria-label="close"
            @click="showDeleteModal = false"
          ></button>
        </header>
        <section class="modal-card-body">
          <p>
            Da li ste sigurni da želite ukloniti sektor
            <strong>{{
              departmentToDelete ? departmentToDelete.departmentName : ""
            }}</strong>
            od ovog korisnika?
          </p>
        </section>
        <footer class="modal-card-foot">
          <button
            class="button is-danger"
            :class="{ 'is-loading': deleting }"
            @click="deleteUserDepartment"
          >
            Obriši
          </button>
          <button class="button" @click="showDeleteModal = false">
            Odustani
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import UserService from "../../services/user.service";

export default {
  name: "EditUserDepartments",

  props: {
    userId: {
      type: Number,
      required: true,
    },
    departments: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      loadingEmployees: false,
      userDepartments: [],
      departmentEmployees: [],
      allEmployees: [],
      selectedDepartment: null,
      selectedEmployees: [],
      formData: {
        userDepartmentId: null,
        userId: null,
        departmentId: null,
        subdepartmentIds: null,
        levelValue: 0,
        note: "",
      },
      isEditing: false,
      errorMessage: "",
      successMessage: "",
      showDeleteModal: false,
      departmentToDelete: null,
    };
  },

  computed: {
    availableDepartments() {
      if (this.isEditing) {
        return this.departments;
      }
      // Filter out departments already assigned to user
      const assignedDeptIds = this.userDepartments.map((ud) => ud.departmentId);
      return this.departments.filter(
        (d) => !assignedDeptIds.includes(d.departmentId)
      );
    },
    canSave() {
      return this.selectedDepartment !== null && !this.saving;
    },
  },

  watch: {
    userId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loadUserDepartments();
        }
      },
    },
  },

  mounted() {
    this.formData.userId = this.userId;
    this.loadAllEmployees();
  },

  methods: {
    async loadUserDepartments() {
      if (!this.userId) return;

      this.loading = true;
      this.errorMessage = "";

      try {
        const response = await UserService.getUserDepartmentsByUserId(
          this.userId
        );
        this.userDepartments = response.data;
      } catch (error) {
        this.errorMessage = this.getErrorMessage(error);
      } finally {
        this.loading = false;
      }
    },

    async loadAllEmployees() {
      try {
        this.allEmployees = this.$store.state.general.employees || [];
      } catch (error) {
        console.error("Error loading employees:", error);
      }
    },

    async onDepartmentChange(department) {
      if (!department) {
        this.departmentEmployees = [];
        return;
      }

      this.formData.departmentId = department.departmentId;
      this.loadingEmployees = true;

      try {
        const response =
          await UserService.getEmployeesForSubdepartmentSelection(
            department.departmentId
          );
        this.departmentEmployees = response.data;
      } catch (error) {
        console.error("Error loading employees for department:", error);
        this.departmentEmployees = [];
      } finally {
        this.loadingEmployees = false;
      }
    },

    editUserDepartment(ud) {
      this.isEditing = true;
      this.formData = {
        userDepartmentId: ud.userDepartmentId,
        userId: ud.userId,
        departmentId: ud.departmentId,
        subdepartmentIds: ud.subdepartmentIds,
        levelValue: ud.levelValue || 0,
        note: ud.note || "",
      };

      // Find and set selected department
      this.selectedDepartment = this.departments.find(
        (d) => d.departmentId === ud.departmentId
      );

      // Load employees for this department and set selected ones
      this.onDepartmentChange(this.selectedDepartment).then(() => {
        if (ud.subdepartmentIds) {
          const empIds = ud.subdepartmentIds
            .split(",")
            .map((id) => parseInt(id.trim()));
          this.selectedEmployees = this.departmentEmployees.filter((e) =>
            empIds.includes(e.employeeId)
          );
        } else {
          this.selectedEmployees = [];
        }
      });
    },

    cancelEdit() {
      this.isEditing = false;
      this.resetForm();
    },

    resetForm() {
      this.formData = {
        userDepartmentId: null,
        userId: this.userId,
        departmentId: null,
        subdepartmentIds: null,
        levelValue: 0,
        note: "",
      };
      this.selectedDepartment = null;
      this.selectedEmployees = [];
      this.departmentEmployees = [];
    },

    async saveUserDepartment() {
      this.saving = true;
      this.errorMessage = "";
      this.successMessage = "";

      // Prepare subdepartmentIds
      if (this.formData.levelValue === 1 && this.selectedEmployees.length > 0) {
        this.formData.subdepartmentIds = this.selectedEmployees
          .map((e) => e.employeeId)
          .join(",");
      } else {
        this.formData.subdepartmentIds = null;
      }

      try {
        if (this.isEditing) {
          await UserService.editUserDepartment(this.formData);
          this.successMessage = "Sektor uspješno ažuriran.";
        } else {
          this.formData.userId = this.userId;
          await UserService.createUserDepartment(this.formData);
          this.successMessage = "Sektor uspješno dodan.";
        }

        await this.loadUserDepartments();
        this.resetForm();
        this.isEditing = false;

        // Clear success message after 3 seconds
        setTimeout(() => {
          this.successMessage = "";
        }, 3000);
      } catch (error) {
        this.errorMessage = this.getErrorMessage(error);
      } finally {
        this.saving = false;
      }
    },

    confirmDeleteUserDepartment(ud) {
      this.departmentToDelete = ud;
      this.showDeleteModal = true;
    },

    async deleteUserDepartment() {
      if (!this.departmentToDelete) return;

      this.deleting = true;
      this.errorMessage = "";

      try {
        await UserService.deleteUserDepartment(
          this.departmentToDelete.userDepartmentId
        );
        this.successMessage = "Sektor uspješno uklonjen.";
        await this.loadUserDepartments();
        this.showDeleteModal = false;
        this.departmentToDelete = null;

        // Clear success message after 3 seconds
        setTimeout(() => {
          this.successMessage = "";
        }, 3000);
      } catch (error) {
        this.errorMessage = this.getErrorMessage(error);
      } finally {
        this.deleting = false;
      }
    },

    getEmployeeNamesFromIds(subdepartmentIds) {
      if (!subdepartmentIds) return "";

      const ids = subdepartmentIds.split(",").map((id) => parseInt(id.trim()));
      const names = [];

      ids.forEach((id) => {
        const emp = this.allEmployees.find((e) => e.id === id);
        if (emp) {
          names.push(emp.name);
        }
      });

      if (names.length === 0) return subdepartmentIds;
      if (names.length <= 3) return names.join(", ");
      return names.slice(0, 3).join(", ") + ` i još ${names.length - 3}...`;
    },

    getErrorMessage(error) {
      return (
        (error.response && error.response.data) ||
        error.message ||
        error.toString()
      );
    },
  },
};
</script>

<style scoped>
.user-departments-container {
  margin-top: 1rem;
}

.add-department-form {
  background-color: #f5f5f5;
  padding: 1rem;
  border-radius: 6px;
}

.mb-4 {
  margin-bottom: 1rem;
}

.mr-1 {
  margin-right: 0.25rem;
}
</style>
